import React from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Badge,
  Button,
  Row,
  Col,
  Progress,
  Spinner,
} from "reactstrap";
import * as Icon from "react-feather";
import PropTypes from "prop-types";

const getBadgeColor = (type, badgeColor) => {
  if (badgeColor) {
    if (badgeColor === "primary") return "light-primary";
    if (badgeColor === "success") return "light-success";
    if (badgeColor === "warning") return "light-warning";
    if (badgeColor === "danger") return "light-danger";
    if (badgeColor === "info") return "light-info";
    return `light-${badgeColor}`;
  }
  switch (type) {
    case "initial_purchase":
    case "active_subscription":
      return "light-primary";
    case "plan_change":
      return "light-success";
    case "renewal":
      return "light-info";
    case "cancellation":
      return "light-warning";
    case "upcoming_expiry":
      return "light-danger";
    case "upcoming_renewal":
    case "scheduled_renewal":
      return "light-primary";
    default:
      return "light-secondary";
  }
};

const renderEventIcon = (type, iconName) => {
  const iconProps = { size: 14 };
  switch (iconName || type) {
    case "CheckCircle":
    case "initial_purchase":
    case "active_subscription":
    case "completed_subscription":
      return <Icon.CheckCircle {...iconProps} className="text-primary" />;
    case "ArrowUpCircle":
    case "plan_change":
      return <Icon.ArrowUpCircle {...iconProps} className="text-success" />;
    case "RefreshCw":
    case "renewal":
      return <Icon.RefreshCw {...iconProps} className="text-info" />;
    case "AlertTriangle":
    case "cancellation":
      return <Icon.AlertTriangle {...iconProps} className="text-warning" />;
    case "Calendar":
    case "upcoming_expiry":
      return <Icon.Calendar {...iconProps} className="text-danger" />;
    case "Clock":
    case "upcoming_renewal":
    case "scheduled_renewal":
      return <Icon.Clock {...iconProps} className="text-primary" />;
    default:
      return <Icon.Circle {...iconProps} className="text-secondary" />;
  }
};

const getEventBorderColor = (badgeColor) => {
  switch (badgeColor) {
    case "danger":
      return "#ea5455";
    case "warning":
      return "#ff9f43";
    case "success":
      return "#28c76f";
    case "info":
      return "#00cfe8";
    case "primary":
    default:
      return "#7367f0";
  }
};

const SubscriptionTimeline = ({
  timelineData,
  onReactivate,
  onUpgradeClick,
  isReactivating,
  upgradeEligible,
}) => {
  if (!timelineData) return null;

  const summary = timelineData.summary || {};
  const events = timelineData.events || [];

  const isCancelled = Boolean(summary.is_cancelled);
  const isExpired = Boolean(summary.is_expired);
  const canReactivate = Boolean(summary.can_reactivate);
  const hasScheduledRenewal = Boolean(summary.has_scheduled_renewal);
  const daysRemaining = summary.remaining_days;

  return (
    <Card className="card-user-timeline shadow-sm">
      <CardHeader className="d-flex justify-content-between align-items-center border-bottom pb-1">
        <div>
          <CardTitle tag="h4" className="mb-25">
            Subscription Timeline & Lifecycle
          </CardTitle>
          <small className="text-muted font-small-2">
            Exact current coverage, remaining time, and paid renewal schedule
          </small>
        </div>
        <div>
          {summary.billing_status === "past_due" ? (
            <Badge color="light-warning" pill className="font-small-2 px-1 py-50">
              <Icon.AlertTriangle size={12} className="mr-25" /> Active in Payment Grace
            </Badge>
          ) : summary.billing_status === "expired" || isExpired ? (
            <Badge color="light-danger" pill className="font-small-2 px-1 py-50">
              <Icon.AlertCircle size={12} className="mr-25" /> Expired
            </Badge>
          ) : summary.billing_status === "cancelled" || isCancelled ? (
            <Badge color="light-warning" pill className="font-small-2 px-1 py-50">
              <Icon.AlertTriangle size={12} className="mr-25" /> Cancelled (Non-Renewing)
            </Badge>
          ) : hasScheduledRenewal ? (
            <Badge color="light-success" pill className="font-small-2 px-1 py-50">
              <Icon.Clock size={12} className="mr-25" /> Active · Renewal Scheduled
            </Badge>
          ) : (
            <Badge color="light-success" pill className="font-small-2 px-1 py-50">
              <Icon.CheckCircle size={12} className="mr-25" /> Active (Auto-Renewing)
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardBody className="pt-2 pb-2">
        <Row className="match-height">
          {/* ─── LEFT COLUMN: Subscription Timeline (Current Period Status) ─── */}
          <Col lg="5" md="6" className="border-right-md pr-lg-2 mb-2 mb-md-0 d-flex flex-column justify-content-between">
            <div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <h5 className="font-weight-bolder text-dark mb-0 d-flex align-items-center font-small-4">
                  <Icon.Clock size={16} className="text-primary mr-50" />
                  Subscription Timeline
                </h5>
                <Badge color="light-primary" pill className="text-capitalize font-small-1">
                  {summary.plan_type || "Annual"}
                </Badge>
              </div>

              {/* Active Plan Summary Box */}
              <div className="bg-light p-1 rounded mb-1 border-0">
                <span className="text-muted font-small-2 text-uppercase d-block mb-25 font-weight-bold">
                  Active Plan
                </span>
                <h5
                  className="font-weight-bolder text-dark mb-25 text-truncate"
                  title={summary.plan_name}
                >
                  {summary.plan_name || "Brand Plan"}
                </h5>
                <div className="d-flex align-items-baseline">
                  <span className="font-weight-bold text-primary font-small-4 mr-50">
                    {summary.amount_formatted || "$0.00"}
                  </span>
                  <span className="text-muted font-small-2">
                    / {summary.plan_type || "Year"}
                  </span>
                </div>
              </div>

              {/* Period & Renewal Key Information */}
              <div className="mb-1">
                <div className="d-flex justify-content-between align-items-center py-50 border-bottom">
                  <span className="text-muted font-small-2 font-weight-bold">
                    <Icon.Calendar size={13} className="mr-50 text-secondary" />
                    Start Date
                  </span>
                  <span className="font-weight-bold font-small-3 text-dark">
                    {summary.start_date || "N/A"}
                  </span>
                </div>

                <div className="d-flex justify-content-between align-items-center py-50 border-bottom">
                  <span className="text-muted font-small-2 font-weight-bold">
                    <Icon.Repeat size={13} className="mr-50 text-secondary" />
                    Active Period
                  </span>
                  <span className="font-weight-bold font-small-3 text-dark text-right">
                    {summary.current_period || "N/A"}
                  </span>
                </div>

                <div className="d-flex justify-content-between align-items-center py-50 border-bottom">
                  <span className="text-muted font-small-2 font-weight-bold">
                    <Icon.CheckCircle size={13} className="mr-50 text-secondary" />
                    {isCancelled ? "Access Expiry Date" : "Next Renewal Date"}
                  </span>
                  <span className="font-weight-bolder font-small-3 text-primary">
                    {summary.renewal_or_expiry_date || "N/A"}
                  </span>
                </div>
              </div>

              {/* Remaining Time & Progress Bar */}
              <div className="bg-light p-1 rounded mb-1">
                <div className="d-flex justify-content-between align-items-center mb-50">
                  <span className="text-muted font-small-2 font-weight-bold text-uppercase">
                    Remaining Time
                  </span>
                  <Badge
                    color={
                      daysRemaining <= 5
                        ? "light-danger"
                        : daysRemaining <= 30
                        ? "light-warning"
                        : "light-info"
                    }
                    pill
                    className="font-small-2 font-weight-bolder"
                  >
                    {summary.remaining_time_label || `${daysRemaining || 0} days remaining`}
                  </Badge>
                </div>
                <Progress
                  value={
                    daysRemaining <= 0
                      ? 0
                      : summary.remaining_percent !== undefined
                      ? summary.remaining_percent
                      : Math.max(0, 100 - (summary.progress_percent || 0))
                  }
                  className="progress-bar-sm"
                  color={
                    daysRemaining <= 5
                      ? "danger"
                      : daysRemaining <= 30
                      ? "warning"
                      : "primary"
                  }
                />
              </div>
            </div>

            {/* Quick Actions Footer */}
            <div className="d-flex flex-wrap gap-50 pt-50">
              {canReactivate && (
                <Button
                  color="success"
                  size="sm"
                  className="mr-50 mb-50 font-small-2 font-weight-bold"
                  onClick={onReactivate}
                  disabled={isReactivating}
                >
                  {isReactivating ? (
                    <>
                      <Spinner size="sm" className="mr-25" /> Reactivating...
                    </>
                  ) : (
                    <>
                      <Icon.CheckCircle size={12} className="mr-25" /> Reactivate
                    </>
                  )}
                </Button>
              )}

              {upgradeEligible && !isCancelled && (
                <Button
                  color="warning"
                  size="sm"
                  className="mb-50 font-small-2 font-weight-bold"
                  onClick={onUpgradeClick}
                >
                  <Icon.Zap size={12} className="mr-25" /> Upgrade to Annual
                </Button>
              )}
            </div>
          </Col>

          {/* ─── RIGHT COLUMN: Lifecycle History & Events (Scrollable) ─── */}
          <Col lg="7" md="6" className="pl-lg-2">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <h5 className="font-weight-bolder text-dark mb-0 d-flex align-items-center font-small-4">
                <Icon.Activity size={16} className="text-primary mr-50" />
                Plan Status & Renewal
              </h5>
              <Badge color="light-secondary" pill className="font-small-2 font-weight-normal">
                {events.length} {events.length === 1 ? "Event" : "Events"}
              </Badge>
            </div>

            {events.length === 0 ? (
              <div className="text-center py-4 text-muted font-small-3 bg-light rounded">
                <Icon.Inbox size={28} className="text-secondary mb-50 d-block mx-auto" />
                No lifecycle events recorded yet.
              </div>
            ) : (
              <div className="subscription-lifecycle-scroll">
                {events.map((event, index) => {
                  const isLast = index === events.length - 1;
                  const isUpcoming =
                    event.type === "upcoming_renewal" ||
                    event.type === "upcoming_expiry" ||
                    event.type === "scheduled_renewal";
                  const isCancelledEvent = event.type === "cancellation";
                  const borderColor = getEventBorderColor(event.badge_color);

                  return (
                    <div
                      key={event.id || index}
                      className="d-flex position-relative mb-1 pb-50"
                      style={{ minHeight: "52px" }}
                    >
                      {/* Connecting Vertical Line */}
                      {!isLast && (
                        <div
                          style={{
                            position: "absolute",
                            left: "14px",
                            top: "28px",
                            bottom: "-8px",
                            width: "2px",
                            backgroundColor: isUpcoming ? "#e2e8f0" : "#ebe9f1",
                            borderStyle: isUpcoming ? "dashed" : "solid",
                          }}
                        />
                      )}

                      {/* Circle Point Indicator */}
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{
                          width: "30px",
                          height: "30px",
                          backgroundColor: isCancelledEvent
                            ? "#fff8e6"
                            : isUpcoming
                            ? "#f8f9fa"
                            : "#ffffff",
                          border: `2px solid ${borderColor}`,
                          zIndex: 2,
                        }}
                      >
                        {renderEventIcon(event.type, event.icon)}
                      </div>

                      {/* Event Content Details */}
                      <div className="ml-1 w-100 pb-50 border-bottom border-light">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                          <div className="d-flex align-items-center flex-wrap mr-50">
                            <span className="font-weight-bolder text-dark font-small-3 mr-50">
                              {event.title}
                            </span>
                            {event.badge && (
                              <Badge
                                color={getBadgeColor(event.type, event.badge_color)}
                                pill
                                className="font-small-1 px-50 py-25 font-weight-bold"
                              >
                                {event.badge}
                              </Badge>
                            )}
                          </div>
                          <div className="d-flex align-items-center font-small-2 text-muted mt-25 mt-sm-0">
                            <span>{event.date}</span>
                            {event.amount && (
                              <span className="font-weight-bolder text-primary ml-50">
                                {event.amount}
                              </span>
                            )}
                          </div>
                        </div>

                        <p className="text-muted font-small-2 mb-0 mt-25">
                          {event.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

SubscriptionTimeline.propTypes = {
  timelineData: PropTypes.object,
  onReactivate: PropTypes.func.isRequired,
  onUpgradeClick: PropTypes.func.isRequired,
  isReactivating: PropTypes.bool,
  upgradeEligible: PropTypes.bool,
};

export default SubscriptionTimeline;
